import { BuildingIcon, FileTextIcon, UsersRoundIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { RelativeTime } from '@/shared/components/date';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';

import type { Cover, PageListItem, SpaceKind } from '../api';
import { highlightParts } from '../lib/highlight';
import { pagePath } from '../lib/links';
import { coverStyle, EMOJI_GROUPS } from '../lib/visuals';

/** A page's emoji (or the default document icon). */
export function PageIcon({ icon, className }: { icon: string | null | undefined; className?: string }) {
  if (icon) {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-flex shrink-0 items-center justify-center leading-none', className)}
      >
        {icon}
      </span>
    );
  }
  return <FileTextIcon aria-hidden="true" className={cn('shrink-0 text-muted-foreground', className)} />;
}

/** A space's emoji on a soft tile. */
export function SpaceIcon({
  icon,
  size = 'md',
  className,
}: {
  icon: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg border bg-card leading-none shadow-xs',
        size === 'sm' && 'size-7 text-base',
        size === 'md' && 'size-10 text-xl',
        size === 'lg' && 'size-14 text-3xl',
        className,
      )}
    >
      {icon}
    </span>
  );
}

export function SpaceKindBadge({ kind }: { kind: SpaceKind }) {
  const { t } = useTranslation('wiki');
  return kind === 'company' ? (
    <Badge variant="soft">
      <BuildingIcon />
      {t('space.kind.company')}
    </Badge>
  ) : (
    <Badge variant="secondary">
      <UsersRoundIcon />
      {t('space.kind.group')}
    </Badge>
  );
}

/** Cover strip of a page (image or gradient); nothing when the page has no cover. */
export function CoverBanner({
  cover,
  className,
  children,
}: {
  cover: Cover | null;
  className?: string;
  children?: ReactNode;
}) {
  const style = coverStyle(cover);
  if (!style) return null;
  return (
    <div data-testid="wiki-cover" className={cn('relative w-full bg-muted', className)} style={style}>
      {children}
    </div>
  );
}

/** Text with the words of a search query marked. */
export function Highlighted({ text, query }: { text: string; query?: string }) {
  if (!query) return <>{text}</>;
  return (
    <>
      {highlightParts(text, query).map((part, index) =>
        part.match ? (
          <mark key={index} className="rounded-[3px] bg-warning-soft px-px text-inherit">
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

/** A page in the home page lists (favorites, recent, updates, drafts, search results). */
export function PageListRow({
  page,
  meta,
  showSpace = true,
  to,
  onSelect,
  excerpt = false,
  highlight,
}: {
  page: PageListItem;
  meta?: ReactNode;
  showSpace?: boolean;
  to?: string;
  onSelect?: () => void;
  /** Show the text excerpt (search results). */
  excerpt?: boolean;
  /** Search query whose words are highlighted in the title and the excerpt. */
  highlight?: string;
}) {
  const { t } = useTranslation('wiki');
  const content = (
    <>
      <PageIcon icon={page.icon} className="mt-0.5 size-5 text-base" />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            <Highlighted text={page.title || t('page.untitled')} query={highlight} />
          </span>
          {!page.isPublished && (
            <Badge variant="warning" className="shrink-0">
              {t('page.unpublished')}
            </Badge>
          )}
        </span>
        <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          {showSpace && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <span aria-hidden="true">{page.space.icon}</span>
              <span className="truncate">{page.space.name}</span>
            </span>
          )}
          {showSpace && <span aria-hidden="true">·</span>}
          {meta ?? <RelativeTime date={page.at ?? page.updatedAt} />}
        </span>
        {excerpt && page.excerpt && (
          <span className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            <Highlighted text={page.excerpt} query={highlight} />
          </span>
        )}
      </span>
    </>
  );
  const className =
    'flex w-full items-start gap-3 rounded-lg px-2.5 py-2 text-left focus-ring transition-colors hover:bg-accent/60';
  if (onSelect) {
    return (
      <button type="button" className={className} onClick={onSelect} data-testid="wiki-page-row">
        {content}
      </button>
    );
  }
  return (
    <Link to={to ?? pagePath(page.id)} className={className} data-testid="wiki-page-row">
      {content}
    </Link>
  );
}

/** Emoji picker for page and space icons. */
export function IconPicker({
  value,
  onChange,
  allowClear = true,
  children,
  align = 'start',
}: {
  value: string | null;
  onChange: (icon: string | null) => void;
  allowClear?: boolean;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
}) {
  const { t } = useTranslation('wiki');
  const [open, setOpen] = useState(false);
  const pick = (icon: string | null) => {
    onChange(icon);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-[min(20.5rem,calc(100vw-2rem))] p-2">
        <div className="max-h-72 space-y-2 overflow-y-auto pr-1" role="listbox" aria-label={t('icon.choose')}>
          {EMOJI_GROUPS.map((group) => (
            <div key={group.id}>
              <p className="px-1 pb-1 text-[0.6875rem] font-medium tracking-wide text-muted-foreground uppercase">
                {t(`icon.groups.${group.id}`)}
              </p>
              <div className="grid grid-cols-9 gap-0.5">
                {group.emoji.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    role="option"
                    aria-selected={emoji === value}
                    aria-label={emoji}
                    onClick={() => pick(emoji)}
                    className={cn(
                      'inline-flex size-8 items-center justify-center rounded-md text-lg leading-none focus-ring hover:bg-accent',
                      emoji === value && 'bg-brand-soft ring-1 ring-brand/40',
                    )}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {allowClear && value && (
          <div className="mt-2 border-t pt-2">
            <Button variant="ghost" size="sm" className="w-full" onClick={() => pick(null)}>
              {t('icon.remove')}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
